import React, { Fragment } from 'react';
import moment from 'moment';
import axios from 'axios';
import { withStyles } from '@material-ui/core/styles';
import { withSnackbar } from 'notistack';
import { withTranslation } from 'react-i18next';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import CloseIcon from '@material-ui/icons/Close';
import Collapse from '@material-ui/core/Collapse';

import AnnouncementConsent from './Announcement/AnnouncementConsent';
import AnnouncementComments from './Announcement/AnnouncementComments';
import AnnouncementReactions from './Announcement/AnnouncementReactions';
import FileAttachmentDownloadDialog from "../shared/FileAttachmentDownloadDialog";
import FileAttachmentRow from "./FileAttachmentRow";

const styles = () => ({
  card: {
    marginBottom: 16,
    padding: 16,
  },
  announcementHeading: {
    marginTop: 10,
    marginBottom: 10,
    fontSize: 28,
    wordWrap: 'break-word',
  },
  announcementSubHeading: {
    fontSize: 18,
    marginTop: 40,
  },
  announcementContent: {
    color: '#394f5a',
    wordBreak: 'break-word',
    '& img': {
      maxWidth: '100%',
    },
    wordWrap: 'break-word',
  },
  announcementDate: {
    color: '#797979',
    fontSize: 14,
  },
  announcementSchoolName: {
    color: '#394f5a',
    marginTop: 8,
    fontWeight: 'bold',
  },
  announcementSchoolLogo: {
    marginRight: 20,
    maxWidth: 56,
  },
  announcementAttachment: {
    '& div': {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    }
  },
  pinImage: {
    width: 24,
    height: 32,
  },
  announcementPriorityPinned: {
    border: '2px solid #ffca28'
  },
});

class AnnouncementCard extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hidden: false,
    };

    this.onClose = this.onClose.bind(this);
  }

  onClose(announcement) {
    const {
      enqueueSnackbar,
    } = this.props;

    axios
      .post(`${window.apiUrl}/dismissannouncement/${announcement.id}`)
      .then((response) => {
        if (response.data.error) {
          enqueueSnackbar(response.data.error, {
            variant: 'error',
            autoHideDuration: 2000,
          });
        } else {
          this.setState({ hidden: true });
          if (this.props.onClose) {
            this.props.onClose();
          }
        }
      })
      .catch((thrown) => {});
  }

  /**
   * TODO convert this to component
   * @param attachment
   * @param event
   */
  onDownloadDialogOpen(attachment, event) {
    if (attachment.scan_status === 'pass') {
      return true;
    }

    event.preventDefault();

    FileAttachmentDownloadDialog(null, {
      scan_status: attachment.scan_status,
      scan_status_message: attachment.scan_status_message
    });
  }

  render() {
    const {
      classes,
      t,
      announcement,
      showForWhichPupils,
    } = this.props;
    const {
      hidden,
    } = this.state;

    const showDismissButton = announcement.sticky === 'no'
      && announcement.state !== 'dismissed'
      && showForWhichPupils;

    let entryClassName = classes.card;
    if (announcement.priority_pinned === 'yes') {
      entryClassName =  `${classes.card} ${classes.announcementPriorityPinned}`;
    }

    let html = announcement.description;
    html = html.replace(/target="[a-z_]+"/g, '');
    if (typeof isIosApp !== 'undefined' && isIosApp) {
      html = html.replace('<a href', '<a target="_system" href');
    } else {
      html = html.replace('<a href', '<a target="_blank" href');
    }

    const description = (
      <Fragment>
        <div
          dangerouslySetInnerHTML={{
            __html: html,
          }}
        />
      </Fragment>
    );

    const dismissGridItemContent = (
      <div>
        {showDismissButton && (
          <IconButton
            aria-label={t('Close')}
            onClick={this.onClose.bind(this, announcement)}
            className="announcement-card-close-button"
          >
            <CloseIcon />
          </IconButton>
        )}
        {announcement.sticky === 'yes' && (
          <img
            src={`${rootUrl}/img/pin.png`}
            className={classes.pinImage}
          />
        )}
      </div>
    );

    const headerGridItemContent = (
      <div>
        <div className={classes.announcementSchoolName}>
          {announcement.school_name}
          {showForWhichPupils && announcement.for_pupils && announcement.for_pupils.length > 0 && (
            <small className={classes.announcementPupils}>
              <span> (</span>
              {announcement.for_pupils.length > 1
                ? t('for pupils')
                : t('for pupil')}
              <span>: </span>
              {announcement.for_pupils.join(', ')}
              <span>)</span>
            </small>
          )}
        </div>
        <div className={classes.announcementDate}>
          {moment(announcement.timestamp).format('MMM D YYYY')}
          <span> - </span>
          <b>{announcement.teacher_name}</b>
        </div>
      </div>
    );

    return (
      <Collapse in={!hidden} className="announcement-card">
        <Paper elevation={1} key={announcement.id} className={entryClassName}>
          <Hidden xsDown>
            <Grid container justify="space-between">
              <Grid item>
                <Grid container>
                  {announcement.school_logo && (
                    <Grid item>
                      <img
                        alt="School Logo"
                        src={announcement.school_logo}
                        className={classes.announcementSchoolLogo}
                      />
                    </Grid>
                  )}
                  <Grid item>
                    {headerGridItemContent}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                {dismissGridItemContent}
              </Grid>
            </Grid>
          </Hidden>
          <Hidden smUp>
            <Grid container justify="space-between">
              {announcement.school_logo && (
                <Grid item>
                  <img
                    alt="School Logo"
                    src={announcement.school_logo}
                    className={classes.announcementSchoolLogo}
                  />
                </Grid>
              )}
              <Grid item>
                {dismissGridItemContent}
              </Grid>
            </Grid>
            <Grid container>
              <Grid item>
                {headerGridItemContent}
              </Grid>
            </Grid>
          </Hidden>
          <Typography
            className={classes.announcementHeading}
            variant="h6"
            component="h4"
          >
            {announcement.title}
          </Typography>
          <Typography className={classes.announcementContent} component="div">
            {description}
          </Typography>
          {announcement.attachments && announcement.attachments.length > 0 && (
            <>
              <Typography
                className={classes.announcementSubHeading}
                variant="h6"
                component="h6"
              >
                {t('Attachments')}
              </Typography>
              <List>
                {announcement.attachments.map((attachment) => {
                  const primary = (
                    <FileAttachmentRow attachment={attachment} />
                  );
                  return (
                    <ListItem
                      button
                      component="a"
                      key={attachment.id}
                      href={attachment.url}
                      target={typeof isIosApp !== 'undefined' && isIosApp ? '_system' : '_blank'}
                      onClick={this.onDownloadDialogOpen.bind(this, attachment)}
                    >
                      <ListItemText primary={primary} />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
          <AnnouncementConsent announcement={announcement} />
          <AnnouncementReactions announcement={announcement} />
          <AnnouncementComments announcement={announcement} />
        </Paper>
      </Collapse>
    );
  }
}

export default withStyles(styles)(
  withSnackbar(withTranslation()(AnnouncementCard))
);
